import React, { useMemo } from 'react'
import { Indicator, UploadElement } from 'is24-corecss'
import { useTranslation } from 'react-i18next'

import { useAppDispatch } from '../../../../../../../../../../app/hooks'
import { AttachmentDataWithRequiredAttachment } from '../../../../../../../../../../@types/types'
import { deleteAttachment, setImageOrder } from '../../../../../../../../../../redux/thunks/multimedia/multimedia'
import { convertImageUrl } from '../../../../../../../../../../services/conversion/conversionServices'
import styles from './UploadedImagesInlineGallery.module.css'
import { useImageListSelection } from './useImageListSelection'

interface UploadedImagesInlineGalleryProps {
  pictureAttachments: AttachmentDataWithRequiredAttachment[]
  onFilesSelected: (files: FileList) => void
  selectedImageId: string | undefined
  onImageSelect: (imageId: string) => void
}

export const UploadedImagesInlineGallery: React.FC<UploadedImagesInlineGalleryProps> = ({
  pictureAttachments,
  onFilesSelected,
  selectedImageId,
  onImageSelect,
}) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const items = useMemo(
    () =>
      pictureAttachments.flatMap(({ attachment }) =>
        attachment.url
          ? [
              {
                id: attachment.id,
                src: convertImageUrl('320', '160', attachment.url) ?? attachment.url,
                alt: attachment.title ?? '',
              },
            ]
          : []
      ),
    [pictureAttachments]
  )
  const imageListSelection = useImageListSelection({
    items,
    selectedImageId,
    onImageSelect,
  })

  const handleRemove = (id: string) => {
    const pictureAttachment = pictureAttachments.find(({ attachment }) => attachment.id === id)
    if (!pictureAttachment) return

    const { attachment } = pictureAttachment
    void dispatch(deleteAttachment(attachment.id, attachment.source, attachment.size))
  }

  const handleReorder = (reorderedItems: Array<{ id: string }>) => {
    dispatch(setImageOrder(reorderedItems.map(({ id }) => id)))
  }

  return (
    <UploadElement.Root
      accept='image/*'
      multiple
      onFileSelect={onFilesSelected}
      showLoader={false}
      status='uploaded'
      data-testid={'inline-image-gallery'}
    >
      <div className={styles.galleryRow}>
        <UploadElement.AddButton size={80} />
        <UploadElement.ImageList
          {...imageListSelection}
          className={styles.imageList}
          items={items}
          thumbnailSize={80}
          onRemove={handleRemove}
          onReorder={handleReorder}
        >
          {items.length > 0 && (
            <div className={styles.titlePictureIndicator}>
              <Indicator.Root appearance='label' color='white' variant='flat' size='small'>
                {t('wizard.screen.multimedia.webview.modal.picture.titlePicture')}
              </Indicator.Root>
            </div>
          )}
        </UploadElement.ImageList>
      </div>
    </UploadElement.Root>
  )
}
